import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  MAX_OPTIONS,
  MIN_OPTIONS,
  blankEditor,
  clientTextStillToWrite,
  editorFromDraft,
  fileBlocker,
  filingBody,
  type EditorOption,
  type InquiryDraftResult,
  type InquiryEditor,
  type MeetingQuestions,
} from "./meetingInquiry.types";

/** The slice of a RAW Feathers service the tab calls. */
interface RawService {
  create: (data: object, params?: object) => Promise<unknown>;
}

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;
const codeOf = (err: unknown): number | null => {
  const c = (err as { code?: unknown } | null)?.code;
  return typeof c === "number" ? c : null;
};

/** What filing answered, in the words the tab shows. */
export interface FiledOutcome {
  id: number;
  /** False: the same question was already open for this client, and the filing joined it. */
  created: boolean;
  /** The open question's own client text was kept (another person may have written it). */
  clientTextIgnored: boolean;
}

/**
 * Meeting Detail → Client questions (#4211): turn a question this processed
 * client meeting left open into a structured client question.
 *
 * THE LIST is read through the `meeting-inquiry-drafts` ServiceStore
 * (meetingIntelligence.stores.ts), keyed by the meeting's id; this store keeps
 * only the EDITOR — the form a person fills in — and the outcome of their acts.
 * Whether a draft can be made at all, and why not, comes from the server.
 *
 * THE ACTS go through the RAW Feathers client, whose refusals reject (so a 403
 * or a 422 can never read as success): an AI draft (`meeting-inquiry-drafts`
 * create, which writes nothing) and the filing (`client-inquiries` create).
 */
class MeetingInquiryStore {
  rootStore: RootStore;
  meetingId: number | null = null;
  loading = false;
  error: string | null = null;
  forbidden = false;

  /** The form; null until the person picks a question (or starts their own). */
  editor: InquiryEditor | null = null;
  /** The options came from an AI draft (said in the filed question's internal note). */
  aiDrafted = false;
  drafting = false;
  draftError: string | null = null;
  /** Other clients the AI draft named: the person removes them before filing. */
  otherClients: string[] = [];

  filing = false;
  fileError: string | null = null;
  filed: FiledOutcome | null = null;

  private loadSeq = 0;
  /**
   * Bumped whenever the form it would land on goes away — another question
   * started, the form closed, another meeting opened, a sign-out. An AI draft or
   * a filing still in flight carries the value it started under, and its answer
   * is dropped if that changed (Codex r1: a slow draft for one meeting must never
   * reappear, filled in, on the next meeting's tab, and be filed for its client).
   */
  private actSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- services ------------------------------------------------------------

  private get service(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      meetingInquiryDraftsStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("meeting-inquiry-drafts") ??
        rs.meetingInquiryDraftsStore) as ServiceStoreType | null | undefined) ?? null
    );
  }

  private raw(path: string): RawService | null {
    const app = (this.rootStore as unknown as { app?: { service: (p: string) => RawService } }).app;
    return app?.service(path) ?? null;
  }

  // ---- derived -------------------------------------------------------------

  /** This meeting's questions, as the server read them. */
  get view(): MeetingQuestions | null {
    if (this.meetingId == null) return null;
    const rows = (this.service?.entities ?? []) as unknown as MeetingQuestions[];
    return rows.find((r) => r.id === this.meetingId) ?? null;
  }

  get fileBlocker(): string | null {
    if (this.view?.blocked) return this.view.blocked;
    if (!this.view?.clientCode) return "This meeting's client has no code in HQ, so a question cannot be filed for it.";
    return fileBlocker(this.editor);
  }

  get canFile(): boolean {
    return !this.filing && !this.drafting && this.fileBlocker === null;
  }

  get canDraft(): boolean {
    return Boolean(this.view?.ai.configured && !this.view.blocked && this.editor?.question.trim()) && !this.drafting && !this.filing;
  }

  /** What the client cannot read yet; filed anyway, finished in the Inquiry Queue. */
  get stillToWrite(): string[] {
    return this.editor ? clientTextStillToWrite(this.editor) : [];
  }

  // ---- loading -------------------------------------------------------------

  setMeetingId(id: number | string | null) {
    const n = Number(id);
    const next = Number.isInteger(n) && n > 0 ? n : null;
    if (next === this.meetingId) return;
    this.dispose();
    this.meetingId = next;
  }

  async load() {
    const ended = sessionMark(this.rootStore);
    const seq = ++this.loadSeq;
    const id = this.meetingId;
    const service = this.service;
    if (id == null) return;
    this.loading = true;
    this.error = null;
    if (!service) {
      this.loading = false;
      this.error = "The meeting-inquiry-drafts service is not available in this build.";
      return;
    }
    try {
      await service.get(id, { throwOnError: true } as never);
      if (ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = false;
      });
    } catch (err) {
      if (isSessionEnded(err) || ended() || seq !== this.loadSeq) return;
      runInAction(() => {
        this.forbidden = codeOf(err) === 403;
        this.error = messageOf(err, "Failed to read this meeting's open questions");
      });
    } finally {
      if (!ended() && seq === this.loadSeq) runInAction(() => (this.loading = false));
    }
  }

  // ---- the editor ----------------------------------------------------------

  /** Start a client question from one of the meeting's open questions — or a blank one. */
  start(question = "") {
    this.actSeq += 1;
    this.editor = blankEditor(question);
    this.aiDrafted = false;
    this.draftError = null;
    this.otherClients = [];
    this.fileError = null;
    this.filed = null;
  }

  close() {
    this.actSeq += 1;
    this.editor = null;
    this.aiDrafted = false;
    this.draftError = null;
    this.otherClients = [];
    this.fileError = null;
  }

  update(patch: Partial<Omit<InquiryEditor, "options">>) {
    if (this.editor) this.editor = { ...this.editor, ...patch };
  }

  updateOption(index: number, patch: Partial<EditorOption>) {
    const e = this.editor;
    if (!e || index < 0 || index >= e.options.length) return;
    this.editor = { ...e, options: e.options.map((o, i) => (i === index ? { ...o, ...patch } : o)) };
  }

  addOption() {
    const e = this.editor;
    if (!e || e.options.length >= MAX_OPTIONS) return;
    this.editor = { ...e, options: [...e.options, { title: "", text: "", implication: "" }] };
  }

  /** Remove one option; the rest are lettered again in order (A, B, …) when filed. */
  removeOption(index: number) {
    const e = this.editor;
    if (!e || e.options.length <= MIN_OPTIONS) return;
    this.editor = { ...e, options: e.options.filter((_, i) => i !== index) };
  }

  // ---- acts ----------------------------------------------------------------

  /** Ask HQ for a first draft of the options. Writes nothing; the person edits what comes back. */
  async draftWithAi() {
    const ended = sessionMark(this.rootStore);
    const id = this.meetingId;
    const e = this.editor;
    const raw = this.raw("meeting-inquiry-drafts");
    if (id == null || !e || !raw || !this.canDraft) return;
    const token = this.actSeq;
    const current = () => !ended() && token === this.actSeq && id === this.meetingId;
    this.drafting = true;
    this.draftError = null;
    try {
      const res = (await raw.create({ meetingId: id, question: e.question })) as InquiryDraftResult;
      // The person moved on (another question, another meeting): this draft is for a form that is gone.
      if (!current()) return;
      runInAction(() => {
        // The question and the person's note are theirs; the fields were locked while drafting.
        this.editor = editorFromDraft(this.editor ?? e, res.draft);
        this.aiDrafted = true;
        this.otherClients = Array.isArray(res.otherClients) ? res.otherClients : [];
      });
    } catch (err) {
      if (isSessionEnded(err) || !current()) return;
      runInAction(() => {
        this.draftError = messageOf(err, "HQ could not draft the options. Write them yourself.");
      });
    } finally {
      if (current()) runInAction(() => (this.drafting = false));
    }
  }

  /** File the client question (stage Internal). Nothing is sent to the client. */
  async file(): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    const e = this.editor;
    const v = this.view;
    const raw = this.raw("client-inquiries");
    if (!e || !v || !raw || !this.canFile) return false;
    const token = this.actSeq;
    const id = this.meetingId;
    const current = () => !ended() && token === this.actSeq && id === this.meetingId;
    this.filing = true;
    this.fileError = null;
    try {
      const res = (await raw.create(filingBody(e, v, this.aiDrafted))) as {
        inquiry?: { id?: number };
        created?: boolean;
        clientTextIgnored?: boolean;
      } | null;
      // Filed, but the person has moved on: the meeting it came from lists it when they return.
      if (!current()) return false;
      const filedId = Number(res?.inquiry?.id);
      if (!Number.isInteger(filedId) || filedId <= 0) throw new Error("HQ did not say which question it filed.");
      runInAction(() => {
        this.filed = { id: filedId, created: res?.created !== false, clientTextIgnored: res?.clientTextIgnored === true };
        this.editor = null;
        this.aiDrafted = false;
        this.otherClients = [];
      });
      await this.load();
      return true;
    } catch (err) {
      if (isSessionEnded(err) || !current()) return false;
      runInAction(() => {
        this.fileError = messageOf(err, "HQ could not file this question.");
      });
      return false;
    } finally {
      // Only this act's flag: a new question or another meeting has its own.
      if (token === this.actSeq && !ended()) runInAction(() => (this.filing = false));
    }
  }

  /** Open a client question in the Inquiry Queue, where its PDF, email and release live. */
  openInQueue(id: number) {
    const router = (this.rootStore as unknown as {
      routerStore?: { goTo?: (name: string, params?: object, query?: object) => unknown };
    }).routerStore;
    void router?.goTo?.("InquiryQueue", {}, { open: String(id) });
  }

  /** Sign-out (RootStore.purgeUserData): every field back to its constructed value. */
  reset() {
    this.dispose();
  }

  dispose() {
    // A read, draft or filing still in flight belongs to the meeting being left: it must not land on the next one.
    this.loadSeq += 1;
    this.actSeq += 1;
    this.meetingId = null;
    this.loading = false;
    this.error = null;
    this.forbidden = false;
    this.editor = null;
    this.aiDrafted = false;
    this.drafting = false;
    this.draftError = null;
    this.otherClients = [];
    this.filing = false;
    this.fileError = null;
    this.filed = null;
  }
}

export default MeetingInquiryStore;
